import { Component, ElementRef, OnDestroy, OnInit, Renderer2, AfterViewInit } from '@angular/core';

// services
import { AuthService } from './../../services/auth.service';
import { NavbarStateService } from './../../services/navbar-state.service';
import { Subscription, filter } from 'rxjs';
import { AppDetailsService } from 'src/app/services/app-details.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'sl-navbar-side',
  templateUrl: './navbar-side.component.html',
  styleUrls: ['./navbar-side.component.less'],
})
export class NavbarSideComponent implements OnInit, OnDestroy, AfterViewInit {
  isCollapsed = true;
  isReverseArrow = false;
  width = 80;
  private navbarStateSubscriptions: Subscription[] = [];

  isAdmin = false;
  isLoggedIn = false;
  hasGoals: boolean | undefined = undefined; // undefined = unknown, false = no goals, true = has goals

  version = '';

  currentRouteSegment!: string | undefined;
  lastVisitedRouteSegment!: string | undefined;

  selectedItem: string = "";
  navbarItems: string[] = ['account', 'goal', 'profile', 'recommendations', 'guilds', 'admin'];

  public get isLandingPage(): boolean {
    const path = location.pathname;
    return path === "/" || path === "/landing";
  }

  constructor(
    private authService: AuthService,
    private navbarState: NavbarStateService,
    private appDetailsService: AppDetailsService,
    private router: Router,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private el: ElementRef
  ) { }

  ngOnInit() {
    // console.log(`******* NavbarSideComponent check for authenticated = ${this.isLoggedIn}`);
    this.isLoggedIn = this.authService.isLoggedIn();

    this.navbarState.initialiseIfNeeded();   // Initialise navbar state service if needed (after auth and router are ready)


    // Subscribe to auth state changes
    this.navbarStateSubscriptions.push(
      this.authService.isLoggedInChange().subscribe({
        next: (ret) => this.loggedInChangedEventFired(ret),
        error: (err) => console.error(`*** NavbarSideComponent: ngOnInit isLoggedInChange Error. Err = ${err}`)
      })
    );

    // Subscribe to navbar state changes (hasGoals and isAdmin)
    this.navbarStateSubscriptions.push(
      this.navbarState.hasGoals.subscribe({
        next: (hasGoals) => {
          this.hasGoals = hasGoals;
        },
        error: (err) => console.error(`*** NavbarSideComponent: Failed to subscribe to hasGoals. Error: ${err}`)
      })
    );

    this.navbarStateSubscriptions.push(
      this.navbarState.isAdmin.subscribe({
        next: (isAdmin) => {
          this.isAdmin = isAdmin;
        },
        error: (err) => console.error(`*** NavbarSideComponent: Failed to subscribe to isAdmin. Error: ${err}`)
      })
    );

    this.appDetailsService.getAppDetailsData().subscribe({
      next: (res) => {
        this.version = res.version;
      },
      error: (err) => console.error(`*** NavbarSideComponent: Failed to get app details. Err = ${err}`)
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          this.updateRouteSelection();
          // Check if navbar state needs to be initialised or reset after navigation
          this.navbarState.initialiseIfNeeded();
          this.navbarState.resetIfNeeded();
        }
      },
      error: (err) => console.error(`*** NavbarSideComponent: router events stream failed. Err = ${err}`)
    });
  }

  ngAfterViewInit() {
    // Set initial selection based on current route after view is initialised
    // This ensures DOM is ready and prevents layout shifts
    this.updateRouteSelection();
  }

  private updateRouteSelection() {
    const root = this.route.root;
    let routeSegment: string | undefined;

    if (root.firstChild) {
      routeSegment = root.firstChild?.snapshot.routeConfig?.path;
    }

    // Fallback: extract from URL if route structure doesn't provide it
    if (!routeSegment) {
      const url = this.router.url || location.pathname;
      routeSegment = this.extractRouteSegment(url);
    }

    if (routeSegment) {
      // Use requestAnimationFrame to batch DOM changes and prevent layout shifts
      const segment = routeSegment; // Capture for closure
      requestAnimationFrame(() => {
        if (this.lastVisitedRouteSegment && this.lastVisitedRouteSegment !== segment) {
          this.removeNavbarItemSelectedClass(this.lastVisitedRouteSegment);
        }
        this.currentRouteSegment = segment;
        this.addNavbarItemSelectedClass(segment);
        this.lastVisitedRouteSegment = segment;
      });
    }
  }

  private extractRouteSegment(url: string): string | undefined {
    // Extract the first segment from URLs like /account/account-details -> account
    const segments = url.split('/').filter(s => s.length > 0);
    if (segments.length > 0) {
      const firstSegment = segments[0];
      // Map route segments to navbar item IDs
      if (this.navbarItems.includes(firstSegment)) {
        return firstSegment;
      }
      // Handle special cases
      if (firstSegment === 'profile') return 'profile';
      if (firstSegment === 'pricing') return 'pricing';
    }
    return undefined;
  }

  ngOnDestroy() {
    // Unsubscribe from all navbar state subscriptions
    this.navbarStateSubscriptions.forEach(sub => {
      if (sub) {
        sub.unsubscribe();
      }
    });
    this.navbarStateSubscriptions = [];
  }

  addNavbarItemSelectedClass(navbarItemId: string) {
    // Handle both span wrapper (dev structure) and direct li (current structure)
    const navbarItem = this.el.nativeElement.querySelector(`#${navbarItemId} > li`) ||
      this.el.nativeElement.querySelector(`#${navbarItemId}`);
    if (navbarItem) {
      this.renderer.addClass(navbarItem, 'ant-menu-item-selected');
    }
  }

  removeNavbarItemSelectedClass(navbarItemId: string) {
    // Handle both span wrapper (dev structure) and direct li (current structure)
    const navbarItem = this.el.nativeElement.querySelector(`#${navbarItemId} > li`) ||
      this.el.nativeElement.querySelector(`#${navbarItemId}`);
    if (navbarItem) {
      this.renderer.removeClass(navbarItem, 'ant-menu-item-selected');
    }
  }

  public login() {
    // console.log(`*** NavBarSide: we are in the navbar-side.component.ts login function`);
    this.authService.login();
  }

  public logout() {
    // console.log(`*** NavBarSide: we are in the navbar-side.component.ts logout function`);
    this.authService.logout();
  }

  public handleAdminClick(event: Event) {
    // Prevent default and stop propagation to ensure our handler runs
    event.preventDefault();
    event.stopPropagation();
    this.navigateToAdmin();
  }

  public navigateToAdmin() {
    this.router.navigate(['/admin']);
  }

  public isProduction(): boolean {
    if (this.version.includes('P:')) {
      return true;
    }
    return false;
  }

  private loggedInChangedEventFired(loggedIn: boolean) {
    // console.log(`*** NavBarSide: isLoggedIn changed value to: ${loggedIn}`);
    this.isLoggedIn = loggedIn;
    // Initialise or reset navbar state based on login status
    if (loggedIn) {
      this.navbarState.initialiseIfNeeded();
    } else {
      this.navbarState.resetIfNeeded();
    }
  }
}
